import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import type { OrgSignin } from "@hub/lib/org-types";
import { ApiError, apiFetch } from "@hub/ui/api";
import { relativeTime } from "@hub/ui/format";
import { consentLinkError, consentView } from "@hub/ui/org-signin";
import { useCallback, useEffect, useState } from "react";

function ConsentBadge({ signin }: { signin: OrgSignin }) {
  const view = consentView(signin);
  switch (view.kind) {
    case "not-applicable":
      return <span className="text-sm text-muted-foreground">{view.reason}</span>;
    case "pending":
      return <Badge variant="outline">Consent pending</Badge>;
    case "granted":
      return <Badge variant="success">Consent granted {relativeTime(view.grantedAt)}</Badge>;
    case "failed":
      return (
        <span className="flex flex-wrap items-center gap-2">
          <Badge variant="warning">
            Consent failed: {view.code} {relativeTime(view.at)}
          </Badge>
          {view.previouslyGranted ? (
            <span className="text-xs text-muted-foreground">an earlier consent still stands</span>
          ) : null}
        </span>
      );
  }
}

/**
 * Existing orgs only: the badge, and the link to send the company's IT
 * administrator. `signin` is the SAVED record (consent is the callback's to
 * write, never the form's).
 */
export function ConsentBlock({ slug, signin }: { slug: string; signin: OrgSignin }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applicable = consentView(signin).kind !== "not-applicable";

  async function copyLink() {
    setError(null);
    try {
      const r = await apiFetch<{ data: { url: string } }>(
        `/api/orgs/${encodeURIComponent(slug)}/admin-consent-link`,
      );
      await navigator.clipboard.writeText(r.data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? consentLinkError(err.status, err.message)
          : err instanceof Error
            ? err.message
            : "request failed",
      );
    }
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      <h3 className="font-medium">Admin consent</h3>
      <div>
        <ConsentBadge signin={signin} />
      </div>
      {applicable ? (
        <>
          <div>
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              {copied ? "Copied" : "Copy consent link"}
            </Button>
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <p className="text-xs text-muted-foreground">
            Send this link to the company's IT administrator. They sign in once as a Global
            Administrator (or Privileged Role / Cloud Application Administrator), approve the listed
            permissions, and see a confirmation page; the badge here updates on the next load.
          </p>
        </>
      ) : null}
    </div>
  );
}

/** An own Microsoft app's client secret: write-only, the stored value is never shown. */
export function SecretBlock({ slug }: { slug: string }) {
  const [updatedAt, setUpdatedAt] = useState<string | null | undefined>(undefined);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLine = useCallback(async () => {
    try {
      const r = await apiFetch<{ data: { key: string; updatedAt: string }[] }>(
        `/api/orgs/${encodeURIComponent(slug)}/secrets`,
      );
      setUpdatedAt(r.data.find((s) => s.key === "ms.client_secret")?.updatedAt ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    }
  }, [slug]);

  useEffect(() => {
    void loadLine();
  }, [loadLine]);

  async function setSecret() {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/orgs/${encodeURIComponent(slug)}/secrets/ms.client_secret`, {
        method: "PUT",
        body: JSON.stringify({ value }),
      });
      setValue("");
      await loadLine();
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      <h3 className="font-medium">App secret</h3>
      {updatedAt === undefined ? null : (
        <p className="text-muted-foreground">
          {updatedAt
            ? `Secret set ${relativeTime(updatedAt)}`
            : "No secret set; boxes cannot be provisioned until it is"}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="password"
          aria-label="Client secret"
          autoComplete="off"
          className="max-w-md"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="button" disabled={busy || value === ""} onClick={setSecret}>
          Set secret
        </Button>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
